import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { authService } from '../services/auth';
import PriceHistoryChart from '../components/PriceHistoryChart';
import apiClient from '../services/api';

export default function ProductDetailPage() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [productId]);

  const loadData = async () => {
    try {
      const [profileData, productsData] = await Promise.all([
        authService.getProfile(),
        apiClient.get('/api/dashboard/products', { params: { limit: 1000 } })
      ]);

      setUser(profileData.member);
      
      const foundProduct = productsData.data.products.find(p => p._id === productId);
      setProduct(foundProduct);
      setLoading(false);
    } catch (error) {
      console.error('Failed to load data:', error);
      navigate('/login');
    }
  };

  const handleLogout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
    sessionStorage.clear();
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{background: '#0B1120'}}>
        <div className="text-center">
          <svg className="w-12 h-12 text-[#3EF1C7] animate-spin mx-auto mb-4" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="text-[#9CB0C7]">Loading product...</p>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{background: '#0B1120'}}>
        <div className="text-center">
          <p className="text-white text-xl mb-4">Product not found</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-6 py-2 bg-[#7A5CFF] text-white rounded-lg hover:bg-[#7A5CFF]/90"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{background: '#0B1120'}}>
      {/* Header */}
      <header className="border-b border-[#22304A] bg-[#0E1426]/80 backdrop-blur">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Product Details</h1>
            <p className="text-sm text-[#9CB0C7]">Product Details</p>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="px-4 py-2 rounded-lg border border-[#22304A] bg-[#111A2E] text-white hover:bg-[#1A2437] transition-colors"
            >
              ← Back to Dashboard
            </button>
            <div className="text-right">
              <p className="text-sm text-white font-medium">{user?.name || user?.email}</p>
              <p className="text-xs text-[#9CB0C7]">{user?.email}</p>
            </div>
            <button
              onClick={handleLogout}
              className="px-4 py-2 rounded-lg border border-[#22304A] bg-[#111A2E] text-white hover:bg-[#1A2437] transition-colors"
            >
              Sign Out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Product Info */}
        <div className="mb-8 rounded-lg border border-[#22304A] bg-[#0E1426]/80 backdrop-blur p-6">
          <div className="flex gap-6">
            <div className="w-32 h-32 bg-[#1A2437] rounded-lg overflow-hidden flex-shrink-0">
              {product.image_url ? (
                <img src={product.image_url} alt={product.title} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-[#9CB0C7]">
                  <svg className="w-16 h-16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                  </svg>
                </div>
              )}
            </div>
            <div className="flex-1">
              <h2 className="text-3xl font-bold text-white mb-2">{product.title}</h2>
              <p className="text-[#9CB0C7] mb-4">{product.category}</p>
              <div className="flex items-center gap-4">
                <div>
                  <div className="text-xs text-[#9CB0C7] mb-1">Current Price</div>
                  <div className="text-2xl font-bold text-white">${product.price.toFixed(2)}</div>
                </div>
                <div>
                  <div className="text-xs text-[#9CB0C7] mb-1">CI Score</div>
                  <div className="text-2xl font-bold text-[#3EF1C7]">{product.ci_score}</div>
                </div>
                <div>
                  <div className="text-xs text-[#9CB0C7] mb-1">Inventory</div>
                  <div className="text-2xl font-bold text-white">{product.inventory || 0}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Price History Chart */}
        <PriceHistoryChart productId={productId} productTitle={product.title} />
      </main>
    </div>
  );
}
